import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiErr } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { GraduationCap, Loader2, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (!token) { setError("Missing or invalid reset token."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="h-10 w-10 rounded-lg bg-[#6366F1] flex items-center justify-center">
            <GraduationCap size={22} className="text-white" />
          </div>
          <span className="font-extrabold tracking-tight text-xl text-white">Enjyme</span>
        </Link>
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-xl">
          {done ? (
            <div className="text-center flex flex-col items-center gap-3" data-testid="reset-done-card">
              <CheckCircle2 className="text-[#22C55E]" size={44} />
              <h1 className="font-display font-extrabold text-2xl tracking-tight text-slate-900">Password updated</h1>
              <p className="text-sm text-slate-500">Your password has been reset. Please sign in with your new password.</p>
              <Link to="/login" data-testid="reset-to-login" className="mt-3 w-full bg-[#6366F1] hover:bg-[#4F46E5] active:scale-[0.98] text-white font-semibold py-2.5 rounded-lg transition-colors">Go to sign in</Link>
            </div>
          ) : (
            <>
              <h1 className="font-display font-extrabold text-2xl tracking-tight text-slate-900">Set a new password</h1>
              <p className="text-sm text-slate-500 mt-1 mb-6">Choose a new password for your account.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">New password</label>
                  <input data-testid="reset-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-300 text-slate-900 focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/20 outline-none transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Confirm password</label>
                  <input data-testid="reset-confirm" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-300 text-slate-900 focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/20 outline-none transition-colors" />
                </div>
                {error && <p data-testid="reset-error" className="text-sm text-red-600">{error}</p>}
                <button data-testid="reset-submit" disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-[#6366F1] hover:bg-[#4F46E5] active:scale-[0.98] text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-60">
                  {loading && <Loader2 size={16} className="animate-spin" />} Update password
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
